import { axiosSecuredInstance } from "configurations/axiosConfig";
import { DownloadFileType } from "utils/data/enum";
import {
  dateTimeAsFilename,
  downloadByBase64,
  getBase64DataType,
} from "utils/shared";

// Mirrors Stewart.Shared.Entities.PolicyData.PaginatedPremiumReceivable
// (PremiumReceivable + ReceivableEntityBase + EntityBase + paging columns).
// PascalCase on the wire because Startup.cs sets
// JsonSerializerOptions.PropertyNamingPolicy = null.
export type PremiumReceivableDto = {
  IdentityKey: number;

  // PremiumReceivable
  PremiumReceivableID: number;
  PolicyHistoryID: number | null;

  // Agent & accounting period
  UnderwriterTypeCode: string | null;
  UnderwriterTypeName: string | null;
  AgentID: string | null;
  AccountDate: string | null;
  AccountingPeriod: string | null;
  AccountingYear: string | null;
  AccountingMonth: string | null;

  // Transaction date
  TransactionDate: string | null;
  TransactionYear: string | null;
  TransactionMonth: string | null;
  TransactionDay: string | null;

  // Policy identification
  PolicyNumber: string | null;
  StateAbbr: string | null;

  // Policy date
  PolicyDate: string | null;
  PolicyYear: string | null;
  PolicyMonth: string | null;
  PolicyYearMonth: string | null;

  // Transaction classification
  PremiumReceivableTypeCode: string | null;
  PremiumReceivableTypeName: string | null;
  IsAccrual: boolean | null;

  // Policy counts
  PolicyCount: number | null;
  SIPolicyCount: number | null;
  // Optional because GetPremiumReceivableListByCriteria does not return this
  // yet — the grid column renders blank until it does. Promote to a required
  // `number | null` (and add it to emptyPremiumReceivableRecord) once the SP
  // includes it.
  ProductCount?: number | null;

  // Financial amounts
  LiabilityAmount: number | null;
  SILiabilityAmount: number | null;
  ActualFee: number | null;
  ActualPremiumTax: number | null;
  ActualRiskRate: number | null;
  ActualRemittance: number | null;
  RemitSplitPct: number | null;

  // Coverage period / received
  CoveringFromDate: string | null;
  CoveringUpToDate: string | null;
  ReceivedDate: string | null;

  // Close audit
  ClosedDate: string | null;

  // Processing flags
  IsExtractedForUDB: boolean | null;
  IsError: boolean | null;
  LastModifiedDate: string | null;

  // Misc
  Comments: string | null;
  PremRecSeqNumber: string | null;

  // Origination
  OriginationSource: string | null;
  OriginationUser: string | null;

  IntegrationKey: string | null;

  // Paging metadata. TotalRows / TotalPages are whole-query totals duplicated
  // on every row by the SP's window functions — not per-page values.
  RowsPerPage: number | null;
  CurrentRow: number | null;
  TotalRows: number | null;
  CurrentPage: number | null;
  TotalPages: number | null;

  // Whole-query aggregates (same value on every row), backing the stats bar.
  TotalPolicyCount: number | null;
  TotalSIPolicyCount: number | null;
  TotalLiabilityAmount: number | null;
  TotalSILiabilityAmount: number | null;
  TotalActualFee: number | null;
  TotalActualRiskRate: number | null;
  TotalActualRemittance: number | null;
};

// Open-ended criteria: arbitrary PremiumReceivableCriteria field names (AgentID,
// AccountingPeriod, TransactionDateFrom, …) flow through via the index signature
// while the known paging/sorting fields stay typed. A name that doesn't match a
// @Param on PremiumReceivable_GetListByCriteria is silently dropped at model
// binding, so keys must match the entity exactly.
export type PremiumReceivableCriteriaDto = {
  UserTimeZone: string;
  RequestedPage?: number;
  ListCount?: number;
  SortColumn?: string;
} & Record<string, unknown>;

// Mirrors Stewart.Shared.Entities.ExportCriteria<PremiumReceivableCriteria>.
export type PremiumReceivableExportCriteriaDto = {
  ExportColumns: string[];
  SearchCriteria: Partial<PremiumReceivableCriteriaDto> & Record<string, unknown>;
};

export type PremiumReceivableDocumentTypeCode = "ADJUSTMENT" | "ACCRUAL";

export const uploadPremiumReceivableDocument = async (
  requestId: string,
  documentTypeCode: PremiumReceivableDocumentTypeCode,
  formData: FormData
): Promise<any> => {
  try {
    const { data } = await axiosSecuredInstance.post(
      "/Document/UploadPremiumReceivable",
      formData,
      {
        params: { requestId, documentTypeCode },
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return data;
  } catch (error) {
    console.error("uploadPremiumReceivableDocument failed", error);
    return null;
  }
};

export const getPremiumReceivableList = async (
  criteria: PremiumReceivableCriteriaDto
): Promise<PremiumReceivableDto[]> => {
  try {
    const { data } = await axiosSecuredInstance.post<PremiumReceivableDto[]>(
      "/PremiumReceivable/GetPremiumReceivableListByCriteria",
      criteria
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("getPremiumReceivableList failed", error);
    return [];
  }
};

export const getPremiumReceivableById = async (
  premiumReceivablesID: number
): Promise<PremiumReceivableDto | null> => {
  try {
    const { data } = await axiosSecuredInstance.get<PremiumReceivableDto>(
      "/PremiumReceivable/GetPremiumReceivableByID",
      { params: { premiumReceivablesID } }
    );
    return data ?? null;
  } catch (error) {
    console.error("getPremiumReceivableById failed", error);
    return null;
  }
};

// Server-side XLSX export. The controller formats TransactionDate /
// ReceivedDate as MM/dd/yyyy and returns a base64 stream.
export const exportPremiumReceivable = async (
  exportCriteria: PremiumReceivableExportCriteriaDto,
  exportTabURL: string,
  fileName: string
): Promise<boolean> => {
  try {
    const { data } = await axiosSecuredInstance.post(
      exportTabURL,
      exportCriteria
    );
    const filename = `${fileName} ${dateTimeAsFilename()}.${DownloadFileType.Xlsx}`;
    downloadByBase64(
      `data:${getBase64DataType(DownloadFileType.Xlsx)};base64,${data}`,
      filename
    );
    return true;
  } catch (error) {
    console.error("exportPremiumReceivable failed", error);
    return false;
  }
};

export const addPremiumReceivable = async (
  entity: Partial<PremiumReceivableDto>,
): Promise<boolean> => {
  try {
    await axiosSecuredInstance.post(
      "/PremiumReceivable/SavePremiumReceivable",
      entity,
    );
    return true;
  } catch (e) {
    console.error("addPremiumReceivable failed", e);
    return false;
  }
};
